// src/utils/services/treatmentPlanAiService.js
import { callOpenAIChat } from './openaiClient';

// Reutilizamos el mismo helper que en HCC
const safeParseJsonFromModel = (rawContent) => {
  if (!rawContent) {
    throw new Error('No content to parse');
  }

  let text = Array.isArray(rawContent)
    ? rawContent
        .map((p) =>
          typeof p === 'string'
            ? p
            : (p && (p.text || p.content || '')) || ''
        )
        .join('')
    : String(rawContent);

  text = text.trim();

  if (text.startsWith('```')) {
    text = text.replace(/^```[a-zA-Z]*\s*/m, '');
    text = text.replace(/```$/m, '');
    text = text.trim();
  }

  const firstBrace = text.indexOf('{');
  const lastBrace = text.lastIndexOf('}');

  if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace) {
    console.error('[safeParseJsonFromModel] No JSON object found in text:', text);
    throw new Error('No JSON object found in model response');
  }

  const jsonSlice = text.slice(firstBrace, lastBrace + 1);
  return JSON.parse(jsonSlice);
};

/**
 * Genera un Treatment Plan estructurado desde contexto clínico
 * (notas + meds + labs + alergias).
 *
 * Devuelve:
 * {
 *   acuteDiagnoses: [
 *     { code, diagnosis, plan, date, provider }
 *   ],
 *   nonHccDiagnoses: [
 *     { condition, plan, date, provider }
 *   ]
 * }
 */
export const generateTreatmentPlanFromAI = async ({ contextText }) => {
  const ctx = contextText || '';

  if (!ctx.trim()) {
    console.warn('[generateTreatmentPlanFromAI] Empty context text');
    return {
      acuteDiagnoses: [],
      nonHccDiagnoses: [],
    };
  }

  const userPrompt = `
You are a physician assistant helping to draft a structured Treatment Plan for a single patient.
All output MUST BE IN ENGLISH.

You will receive a merged clinical context containing:
- Multiple encounter notes (Annual Wellness, Follow Up, Telehealth, Hospital Discharge).
- A list of medications (current and historic).
- Laboratory results.
- Allergy information.

Your task:
1. Identify the most relevant **acute or temporary problems** that require a short-term treatment plan.
2. Identify other **non-HCC or non-major chronic conditions** that still need a documented plan.
3. Propose a short, clinically reasonable plan for each problem, based ONLY on the available information.
4. DO NOT prescribe exact drug dosages. Use phrases like "consider starting", "optimize current therapy", "evaluate adjustment" instead of giving exact mg.

Return ONLY a JSON object with this structure:

{
  "acuteDiagnoses": [
    {
      "code": "ICD-10 code if you can infer it, or 'N/A'",
      "diagnosis": "Short name of the acute or temporary condition (English)",
      "plan": "Concise treatment / follow-up plan (professional tone, English)",
      "date": "Suggested plan date in YYYY-MM-DD or 'N/A'",
      "provider": "Generic provider description (e.g. 'PCP', 'Cardiology', 'Telehealth visit')"
    }
  ],
  "nonHccDiagnoses": [
    {
      "condition": "Condition or complaint name (English)",
      "plan": "Concise plan or follow-up strategy for this condition (English)",
      "date": "Suggested plan date in YYYY-MM-DD or 'N/A'",
      "provider": "Generic provider description if relevant"
    }
  ]
}

Important rules:
- DO NOT write anything outside of the JSON.
- Use only information compatible with the clinical context. If you are unsure about exact ICD-10, set "code": "N/A".
- Keep plans short, actionable and professional, not patient-facing education.
- Do not exceed 10 items in each list (acuteDiagnoses and nonHccDiagnoses).

===== MERGED CLINICAL CONTEXT (NOTES + MEDS + LABS + ALLERGIES) =====

${ctx}
`;

  const data = await callOpenAIChat({
    model: 'gpt-4o-mini',
    messages: [{ role: 'user', content: userPrompt }],
    temperature: 0.2,
  });

  const content = data?.choices?.[0]?.message?.content;
  console.log('[generateTreatmentPlanFromAI] RAW content:', content);

  if (!content) {
    console.error('[generateTreatmentPlanFromAI] Empty response from OpenAI');
    throw new Error('Empty response from OpenAI');
  }

  try {
    const json = safeParseJsonFromModel(content);

    return {
      acuteDiagnoses: Array.isArray(json.acuteDiagnoses)
        ? json.acuteDiagnoses
        : [],
      nonHccDiagnoses: Array.isArray(json.nonHccDiagnoses)
        ? json.nonHccDiagnoses
        : [],
    };
  } catch (err) {
    console.error('[generateTreatmentPlanFromAI] Error parsing JSON:', err);
    return {
      acuteDiagnoses: [],
      nonHccDiagnoses: [],
    };
  }
};
